import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { type UserResponse } from "@/api/auth";
import { AppShell } from "@/components/AppShell";
import { meQuery, requireAuth } from "@/lib/auth-guard";
import { IconFacebook, IconGooglePhotos, IconCircle } from "@/components/icons";

/**
 * Featured cards row, mirroring Google Photos' "memory tiles" strip
 * across the top of the main scroll region. Each tile links to one
 * MVP surface. Those surfaces are placeholder routes until Phase B/C
 * land — the visual frame is here so the shape is legible early.
 */
const FEATURED = [
  {
    title: "Bring in from Facebook",
    subtitle: "Import photos in a few clicks",
    to: "/imports",
    tone: "sage",
    Icon: IconFacebook,
  },
  {
    title: "Bring in from Google Photos",
    subtitle: "Sync an album at a time",
    to: "/imports",
    tone: "taupe",
    Icon: IconGooglePhotos,
  },
  {
    title: "Immediate Family Circle",
    subtitle: "Invite the people who matter",
    to: "/circle",
    tone: "sand",
    Icon: IconCircle,
  },
] as const;

const toneStyles: Record<string, string> = {
  sage: "from-sage/40 to-sage/10",
  taupe: "from-taupe/40 to-taupe/10",
  sand: "from-sand/60 to-sand/20",
};

const Home = () => {
  const { data } = useSuspenseQuery<UserResponse>(meQuery);
  const firstName = data.name?.split(" ")[0] ?? "there";

  return (
    <AppShell user={data}>
      {/* Greeting */}
      <section className="mb-8">
        <p className="font-mono text-[13px] uppercase tracking-[0.18em] text-taupe-deep">
          Welcome back
        </p>
        <h1
          className="mt-1 font-serif text-[40px] leading-[44px] text-ink"
          style={{ fontWeight: 600 }}
        >
          Hello,{" "}
          <em className="text-blush" style={{ fontStyle: "italic" }}>
            {firstName}
          </em>
          .
        </h1>
      </section>

      {/* Featured cards row */}
      <section aria-label="Get started" className="mb-10">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURED.map((card) => (
            <Link
              key={card.title}
              to={card.to}
              className={`group relative flex h-44 flex-col justify-end rounded-2xl bg-gradient-to-br ${toneStyles[card.tone]} p-5 transition-transform hover:-translate-y-0.5`}
            >
              <div className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/70 text-ink">
                <card.Icon className="h-5 w-5" />
              </div>
              <h3
                className="font-serif text-[20px] leading-[24px] text-ink"
                style={{ fontWeight: 600 }}
              >
                {card.title}
              </h3>
              <p className="mt-1 font-sans text-[13px] text-ink-soft">
                {card.subtitle}
              </p>
            </Link>
          ))}
        </div>
      </section>

      {/* Photo grid — empty state placeholder for the MVP */}
      <section aria-label="Your memories">
        <div className="mb-4 flex items-baseline justify-between">
          <h2
            className="font-serif text-[22px] uppercase text-ink"
            style={{ fontWeight: 600, letterSpacing: "0.06em" }}
          >
            Your memories
          </h2>
          <span className="font-mono text-[13px] uppercase tracking-[0.18em] text-taupe-deep">
            {data.families.length} circle
            {data.families.length === 1 ? "" : "s"}
          </span>
        </div>

        <div className="rounded-2xl border border-dashed border-sand bg-white/60 p-10 text-center">
          <p className="mx-auto max-w-sm font-serif text-[20px] italic leading-[28px] text-ink-soft">
            "Saving a memory is not nostalgia. It is provision."
          </p>
          <p className="mt-6 font-sans text-[14px] text-ink-soft">
            Nothing kept yet. Bring in a photo from Facebook or Google Photos to
            start.
          </p>
        </div>
      </section>
    </AppShell>
  );
};

export const Route = createFileRoute("/home")({
  loader: ({ context }) => requireAuth(context.queryClient),
  component: Home,
});
